import { Link, useLocation } from "react-router-dom";
import style from "./index.module.css";
import { useTranslation } from "../../../hooks/useTranslation";
import {
  VehicleConditions,
  VehicleTypesT,
  VehicleNamesPluralT,
  vehicleModules,
  VehicleNames,
  vehicleConditions,
} from "../../../utils/GeneralTypes";
import { useEffect, useMemo, useRef, useState } from "react";
import { findVehicles, findVehiclesCount } from "../../../utils/fetcher";

const VehicleTypesControllers = ({
  active,
  baseLink,
  postLink,
  isBtn = false,
  onClickHandler,
  excludedVehicles,
}: ComponentPropsType) => {
  return (
    <div className={style.controllers}>
      {vehicleModules.map(({ single, Icon, plural }) => {
        const isActive = active === single;
        if (isActive) {
          return (
            <span key={`${single}-vehiclecodition-controller-active`}>
              <Icon width="35" height="35" color="#dc0000" />
            </span>
          );
        } else if (isBtn && onClickHandler && !excludedVehicles?.includes(single)) {
          return (
            <button
              key={`${single}-vehiclecodition-controller-button`}
              type="button"
              onClick={() => onClickHandler(single)}
            >
              <Icon width="35" height="35" color="#000000" />
            </button>
          );
        } else if (!excludedVehicles?.includes("bus")) {
          return (
            <Link
              key={`${single}-vehiclecodition-controller-default`}
              to={`${baseLink}/${plural}${postLink ? `/${postLink}` : ""}`}
            >
              <Icon width="35" height="35" color="#000000" />
            </Link>
          );
        }
        return null;
      })}
    </div>
  );
};

export const VehicleTypeSelector = ({
  active,
  onClickHandler,
  availableVehicles,
}: {
  active: string;
  onClickHandler: CallableFunction;
  availableVehicles: VehicleTypesT[];
}) => {
  return (
    <div className={style.controllers}>
      {vehicleModules.map(({ single, Icon }) => {
        const isActive = active === single;

        if (availableVehicles?.includes(single) && isActive) {
          return (
            <span key={`${single}-vehiclecodition-controller-active`}>
              <Icon width="35" height="35" color="#dc0000" />
            </span>
          );
        } else if (availableVehicles?.includes(single)) {
          return (
            <button
              key={`${single}-vehiclecodition-controller-button`}
              type="button"
              onClick={() => onClickHandler(single)}
            >
              <Icon width="35" height="35" color="#000000" />
            </button>
          );
        }
        return null;
      })}
    </div>
  );
};

export const VehicleConditionsControllers = ({
  active,
  baseLink,
  isCombinedLink = false,
  extraParams = "",
  ...isForListingsData
}: {
  active: VehicleConditions;
  baseLink: string;
  isCombinedLink?: boolean;
  extraParams?: string;
} & (
  | {
      isForListings: true;
      vehicleNames: VehicleNames;
    }
  | {
      isForListings?: false;
    }
)) => {
  const initialMount = useRef<boolean>(true);
  const { locale } = useTranslation();
  const { search: historySearch } = useLocation();
  const searchParams = useMemo(() => {
    return new URLSearchParams(historySearch);
  }, [historySearch]);
  const [componentDataForListings, setComponentDataForListings] = useState<{
    isFetching: boolean;
    data: {
      // TODO: add type to item instead of any
      listingsCount: {
        used: number;
        new: number;
        imported: number;
      };
    };
  }>({
    isFetching: true,
    data: {
      listingsCount: {
        used: 0,
        new: 0,
        imported: 0,
      },
    },
  });

  useEffect(() => {
    (async () => {
      if (initialMount.current && isForListingsData.isForListings) {
        const res = await Promise.all(
          vehicleConditions.map((condition) => {
            const search = new URLSearchParams(searchParams);
            search.set("only_count", "1");
            if (condition === "new") search.set("for_distributor", "1");
            return findVehiclesCount(condition, isForListingsData.vehicleNames.single, `?${search.toString()}`);
          }),
        );

        setComponentDataForListings(() => ({
          isFetching: false,
          data: {
            listingsCount: {
              [vehicleConditions[0]]: res[0].data.count,
              [vehicleConditions[1]]: res[1].data.count,
              [vehicleConditions[2]]: res[2].data.count,
            },
          },
        }));
        initialMount.current = false;
      }
    })();
  }, [searchParams, isForListingsData]);

  return (
    <div className={style.controllers}>
      <div>
        {isForListingsData.isForListings ? (
          <p className={style.vehicles_count}>{componentDataForListings.data.listingsCount.new}</p>
        ) : null}
        {active === "new" ? (
          <span>{locale("new")}</span>
        ) : (
          <Link to={isCombinedLink ? `/new-${baseLink}${extraParams}` : `${baseLink}/new${extraParams}`}>
            {locale("new")}
          </Link>
        )}
      </div>
      <div>
        {isForListingsData.isForListings ? (
          <p className={style.vehicles_count}>{componentDataForListings.data.listingsCount.used}</p>
        ) : null}
        {active === "used" ? (
          <span>{locale("used")}</span>
        ) : (
          <Link to={isCombinedLink ? `/used-${baseLink}${extraParams}` : `${baseLink}/used${extraParams}`}>
            {locale("used")}
          </Link>
        )}
      </div>
      <div>
        <p className={style.vehicles_count}>{componentDataForListings.data.listingsCount.imported}</p>
        {active === "imported" ? (
          <span>{locale("imported")}</span>
        ) : (
          <Link to={isCombinedLink ? `/imported-${baseLink}${extraParams}` : `${baseLink}/imported${extraParams}`}>
            {locale("imported")}
          </Link>
        )}
      </div>
    </div>
  );
};

export const VehicleConditionSelector = ({
  active,
  onClickHandler,
}: {
  active: string;
  onClickHandler: CallableFunction;
}) => {
  const { locale } = useTranslation();
  return (
    <div className={style.controllers}>
      {active === "new" ? (
        <span>{locale("new")}</span>
      ) : (
        <button type="button" onClick={() => onClickHandler("new")} key={"new"}>
          {locale("new")}
        </button>
      )}
      {active === "used" ? (
        <span>{locale("used")}</span>
      ) : (
        <button type="button" onClick={() => onClickHandler("used")} key={"used"}>
          {locale("used")}
        </button>
      )}
      {active === "imported" ? (
        <span>{locale("imported")}</span>
      ) : (
        <button type="button" onClick={() => onClickHandler("imported")} key={"imported"}>
          {locale("imported")}
        </button>
      )}
    </div>
  );
};

export default VehicleTypesControllers;

type ComponentPropsType = {
  active: VehicleTypesT;
  excludedVehicles?: string[];
  baseLink: string;
  postLink?: string;
  isBtn?: boolean;
  onClickHandler?: CallableFunction;
};
